import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import Header from '../../components/Header';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import FilterPanel from './components/FilterPanel';
import SearchBar from './components/SearchBar';
import OpportunityCard from './components/OpportunityCard';
import MapView from './components/MapView';
import SavedOpportunities from './components/SavedOpportunities';
import ViewToggle from './components/ViewToggle';
import SortDropdown from './components/SortDropdown';

import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';

const SmartOpportunityFinder = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [filters, setFilters] = useState({
    skills: [],
    causes: [],
    timeCommitment: '',
    location: '',
    remoteOnly: false,
    weekendOnly: false,
    highImpact: false
  });

  const [searchQuery, setSearchQuery] = useState('');
  const [currentView, setCurrentView] = useState('grid');
  const [sortBy, setSortBy] = useState('match');
  const [isMobileFilterOpen, setIsMobileFilterOpen] = useState(false);
  const [savedCount, setSavedCount] = useState(0);
  const [opportunities, setOpportunities] = useState([]);

  /* Mock data removed - fetching from DB */

  useEffect(() => {
    const fetchOpportunities = async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/opportunities`);
        const data = await res.json();
        // Since DB model fields might slightly differ from frontend props, we might need mapping if keys don't match.
        // But our seed data keys (title, organization, etc.) mostly match the card expectations.
        // Mongoose _id needs to be mapped to id if component expects id.
        const mappedData = data.map(op => ({
          ...op,
          id: op._id,
          organizationName: op.organization, // Frontend expects 'organizationName'
          matchPercentage: op.matchScore,    // Frontend expects 'matchPercentage'
          skillsMatch: op.matchScore         // Reuse score
        }));

        setOpportunities(mappedData);
      } catch (error) {
        console.error("Error fetching opportunities:", error);
      }
    };

    fetchOpportunities();
  }, []);

  const handleFilterChange = (filterName, value) => {
    setFilters((prev) => ({
      ...prev,
      [filterName]: value
    }));
  };

  const handleClearFilters = () => {
    setFilters({
      skills: [],
      causes: [],
      timeCommitment: '',
      location: '',
      remoteOnly: false,
      weekendOnly: false,
      highImpact: false
    });
    setSearchQuery('');
  };

  const handleSearch = (query) => {
    setSearchQuery(query);
  };

  const handleSaveOpportunity = (id, isSaved) => {
    setOpportunities((prev) =>
      prev?.map((opp) =>
        opp?.id === id ? { ...opp, isSaved } : opp
      )
    );
    setSavedCount((prev) => isSaved ? prev + 1 : prev - 1);
  };

  const handleApply = async (id) => {
    if (!user) {
      const confirmLogin = window.confirm("You need to login to apply. Go to login?");
      if (confirmLogin) navigate('/login');
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/applications`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${user.token}`
        },
        body: JSON.stringify({ opportunityId: id })
      });

      const data = await response.json();

      if (response.ok) {
        alert("Application Successful!");
      } else {
        alert(data.message || "Application failed");
      }
    } catch (error) {
      alert("Application failed: Network Error");
    }
  };

  const handleViewSaved = () => {
    alert(`You have ${savedCount} saved opportunities.\n\n(Saved view showing placeholder data for now)`);
  };

  const handleViewDetails = (id) => {
    const opportunity = opportunities.find(o => o.id === id);
    if (opportunity) {
      alert(`Details for: ${opportunity.title}\n\nLocation: ${opportunity.location}\nSchedule: ${opportunity.schedule}\nDescription: ${opportunity.description}`);
    }
  };

  const handleLoadMore = () => {
    alert("No more opportunities to load.");
  };

  const filteredOpportunities = opportunities?.filter((opp) => {
    if (searchQuery && !opp?.title?.toLowerCase()?.includes(searchQuery?.toLowerCase()) &&
      !opp?.description?.toLowerCase()?.includes(searchQuery?.toLowerCase()) &&
      !opp?.organizationName?.toLowerCase()?.includes(searchQuery?.toLowerCase())) {
      return false;
    }

    if (filters?.causes?.length > 0 && !filters?.causes?.includes(opp?.cause?.toLowerCase()?.replace(' & ', '-')?.replace(' ', '-'))) {
      return false;
    }

    if (filters?.remoteOnly && !opp?.isRemote) {
      return false;
    }

    if (filters?.highImpact && opp?.impactLevel !== 'High') {
      return false;
    }

    return true;
  });

  const sortedOpportunities = [...filteredOpportunities]?.sort((a, b) => {
    switch (sortBy) {
      case 'match':
        return b?.matchPercentage - a?.matchPercentage;
      case 'urgent':
        return (b?.isUrgent ? 1 : 0) - (a?.isUrgent ? 1 : 0);
      case 'impact':
        const impactOrder = { 'High': 3, 'Medium': 2, 'Low': 1 };
        return impactOrder?.[b?.impactLevel] - impactOrder?.[a?.impactLevel];
      default:
        return 0;
    }
  });

  return (
    <>
      <Helmet>
        <title>Smart Opportunity Finder - VolunteerHub</title>
        <meta name="description" content="Find the perfect volunteer opportunity with AI-powered matching. Connect your skills with meaningful causes and make a real impact in your community." />
      </Helmet>
      <div className="min-h-screen bg-background">
        <Header />

        <main className="main-content">
          <div className="flex">
            <FilterPanel
              filters={filters}
              onFilterChange={handleFilterChange}
              onClearFilters={handleClearFilters}
              isMobileOpen={isMobileFilterOpen}
              onMobileClose={() => setIsMobileFilterOpen(false)} />


            <div className="flex-1 min-w-0">
              <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
                <div className="space-y-4">
                  <div>
                    <h1 className="text-3xl sm:text-4xl font-bold text-foreground mb-2">
                      Find Your Perfect Opportunity
                    </h1>
                    <p className="text-lg text-muted-foreground">
                      AI-powered matching connects your skills with meaningful impact
                    </p>
                  </div>

                  <SearchBar
                    onSearch={handleSearch}
                    onFilterToggle={() => setIsMobileFilterOpen(true)} />

                </div>

                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                  <ViewToggle
                    currentView={currentView}
                    onViewChange={setCurrentView}
                    resultsCount={sortedOpportunities?.length} />

                  <SortDropdown value={sortBy} onChange={setSortBy} />
                </div>

                {sortedOpportunities?.length === 0 ?
                  <div className="text-center py-16">
                    <div className="w-24 h-24 mx-auto mb-6 rounded-full bg-muted flex items-center justify-center">
                      <Icon name="Search" size={48} color="var(--color-muted-foreground)" />
                    </div>
                    <h3 className="text-xl font-semibold text-foreground mb-2">
                      No opportunities found
                    </h3>
                    <p className="text-muted-foreground mb-6">
                      Try adjusting your filters or search terms
                    </p>
                    <Button
                      variant="outline"
                      onClick={handleClearFilters}
                      iconName="RotateCcw"
                      iconPosition="left">

                      Clear All Filters
                    </Button>
                  </div> :

                  <div className={`
                    ${currentView === 'grid' ? 'grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6' : 'space-y-4'}
                  `}>
                    {sortedOpportunities?.map((opportunity) =>
                      <OpportunityCard
                        key={opportunity?.id}
                        opportunity={opportunity}
                        onSave={handleSaveOpportunity}
                        onApply={handleApply}
                        onViewDetails={handleViewDetails} />

                    )}
                  </div>
                }

                {sortedOpportunities?.length > 0 &&
                  <div className="flex justify-center pt-8">
                    <Button
                      variant="outline"
                      size="lg"
                      iconName="RefreshCw"
                      iconPosition="left"
                      onClick={handleLoadMore}>

                      Load More Opportunities
                    </Button>
                  </div>
                }
              </div>
            </div>
          </div>

          {currentView === 'map' &&
            <MapView
              opportunities={sortedOpportunities}
              onMarkerClick={(id) => console.log('Marker clicked:', id)}
              onClose={() => setCurrentView('grid')} />

          }

          <SavedOpportunities
            count={savedCount}
            onViewSaved={handleViewSaved} />

        </main>
      </div>
    </>);

};

export default SmartOpportunityFinder;